import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./DriverManager.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faKey,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { BASE_AUTH_URL, BASE_DRIVER_URL } from "~/helper/apiService";
import Skeleton from "~/components/Skeleton/Skeleton";

const cx = classNames.bind(styles);

function DriverManager() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    email: "",
    license_plate: "",
    model: "",
    password: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const { showToast } = useContext(ToastContext);
  const { showModal, hideModal } = useContext(ModalContext);

  // Lấy danh sách tài xế
  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_DRIVER_URL}drivers`, {
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        setDrivers(data.drivers || []);
      } else {
        showToast(data.message || "Lỗi khi lấy danh sách tài xế", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Xử lý thay đổi input trong form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Xử lý submit form (thêm hoặc sửa tài xế)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const url = isEditing
      ? `${BASE_DRIVER_URL}drivers/${formData.id}`
      : `${BASE_DRIVER_URL}drivers`;
    const method = isEditing ? "PUT" : "POST";

    const requestBody = {
      name: formData.name,
      email: formData.email,
      license_plate: formData.license_plate,
      model: formData.model,
    };
    if (!isEditing) {
      requestBody.password = formData.password;
    }

    try {
      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message, "success");
        fetchDrivers();
        resetForm();
      } else {
        showToast(data.message || "Lỗi khi xử lý tài xế", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa tài xế
  const handleDelete = async (id, name) => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_DRIVER_URL}drivers/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        showToast(data.message, "success");
        fetchDrivers();
      } else {
        showToast(data.message || "Lỗi khi xóa tài xế", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Xác nhận xóa tài xế
  const confirmDelete = (id, name) => {
    showModal({
      title: "Xác Nhận Xóa Tài Xế",
      message: `Bạn có chắc chắn muốn xóa tài xế ${name}?`,
      onConfirm: () => handleDelete(id, name),
      onCancel: null,
    });
  };

  // Xử lý sửa tài xế
  const handleEdit = (driver) => {
    setFormData({
      id: driver.id,
      name: driver.name,
      email: driver.email,
      license_plate: driver.license_plate,
      model: driver.model,
      password: "",
    });
    setIsEditing(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: "",
      name: "",
      email: "",
      license_plate: "",
      model: "",
      password: "",
    });
    setIsEditing(false);
  };

  // Xử lý reset mật khẩu
  const handleResetPassword = (driver) => {
    console.log(driver)
    showModal({
      title: `Đặt Lại Mật Khẩu Cho ${driver.name}`,
      message: (
        <div>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            placeholder="Mật khẩu mới"
            className={cx("input")}
            onChange={(e) => {
              document.getElementById("newPassword").value = e.target.value;
            }}
          />
        </div>
      ),
      onConfirm: async () => {
        const newPassword = document.getElementById("newPassword").value;
        // if (!newPassword || newPassword.length < 8) {
        //   showToast("Mật khẩu mới phải có ít nhất 8 ký tự", "error");
        //   return;
        // }
        setLoading(true);
        try {
          const response = await fetch(`${BASE_AUTH_URL}reset-password`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ user_id: driver.user_id, newPassword }),
          });
          const data = await response.json();
          if (response.ok) {
            showToast(data.message, "success");
          } else {
            showToast(data.error || "Lỗi khi đặt lại mật khẩu", "error");
          }
        } catch (error) {
          showToast("Lỗi kết nối: " + error.message, "error");
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {hideModal()},
      confirmText: "Xác Nhận",
      cancelText: "Hủy",
    });
  };

  return (
    <div className={cx("driver-manager")}>
      <h2 className={cx("subtitle")}>
        {isEditing ? "Sửa Thông Tin Tài Xế" : "Thêm Tài Xế Mới"}
      </h2>
      <form className={cx("search-form")} onSubmit={handleSubmit}>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Tên tài xế"
          className={cx("input")}
          disabled={loading}
        />
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Email"
          className={cx("input")}
          disabled={loading}
        />
        <input
          type="text"
          name="license_plate"
          value={formData.license_plate}
          onChange={handleInputChange}
          placeholder="Biển số xe"
          className={cx("input")}
          disabled={loading}
        />
        <input
          type="text"
          name="model"
          value={formData.model}
          onChange={handleInputChange}
          placeholder="Mẫu xe"
          className={cx("input")}
          disabled={loading}
        />
        {!isEditing && (
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Mật khẩu"
            className={cx("input")}
            disabled={loading}
          />
        )}
        <div className={cx("button-container")}>
          <button type="submit" className={cx("button")} disabled={loading}>
            {isEditing ? "Cập Nhật" : "Thêm Tài Xế"}
          </button>
          {isEditing && (
            <button
              type="button"
              className={cx("button", "back-button")}
              onClick={resetForm}
              disabled={loading}
            >
              Hủy
            </button>
          )}
        </div>
      </form>

      <h2 className={cx("subtitle")}>Danh Sách Tài Xế</h2>
      {loading ? (
        <table className={cx("table")}>
          <thead>
            <tr className={cx("tr")}>
              <th className={cx("th")}>Tên</th>
              <th className={cx("th")}>Email</th>
              <th className={cx("th")}>Biển Số Xe</th>
              <th className={cx("th")}>Mẫu Xe</th>
              <th className={cx("th")}>Hành Động</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, idx) => (
              <tr key={idx} className={cx("tr")}>
                <td className={cx("td")}>
                  <Skeleton height={14} width="70%" />
                </td>
                <td className={cx("td")}>
                  <Skeleton height={14} width="80%" />
                </td>
                <td className={cx("td")}>
                  <Skeleton height={14} width="60%" />
                </td>
                <td className={cx("td")}>
                  <Skeleton height={14} width="50%" />
                </td>
                <td className={cx("td")}>
                  <div className={cx("action")}>
                    <Skeleton height={28} width={28} radius={6} />
                    <Skeleton height={28} width={28} radius={6} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : drivers.length === 0 ? (
        <div className={cx("no-data")}>Không có tài xế nào</div>
      ) : (
        <table className={cx("table")}>
          <thead>
            <tr className={cx("tr")}>
              <th className={cx("th")}>Tên</th>
              <th className={cx("th")}>Email</th>
              <th className={cx("th")}>Biển Số Xe</th>
              <th className={cx("th")}>Mẫu Xe</th>
              <th className={cx("th")}>Hành Động</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((driver) => (
              <tr key={driver.id} className={cx("tr")}>
                <td className={cx("td")}>{driver.name}</td>
                <td className={cx("td")}>{driver.email}</td>
                <td className={cx("td")}>{driver.license_plate}</td>
                <td className={cx("td")}>{driver.model}</td>
                <td className={cx("td")}>
                  <div className={cx("action")}>
                    <button
                      className={cx("edit-button")}
                      onClick={() => handleEdit(driver)}
                      title="Sửa"
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    {/* <button
                      className={cx("delete-button")}
                      onClick={() => confirmDelete(driver.id, driver.name)}
                      title="Xóa"
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button> */}
                    <button
                      className={cx("reset-password-button")}
                      onClick={() => handleResetPassword(driver)}
                      title="Đặt lại mật khẩu"
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faKey} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default DriverManager;